import {
  Component,
  EventEmitter,
  Inject,
  InjectionToken,
  Input,
  OnDestroy,
  OnInit,
  Output,
  StaticProvider,
} from '@angular/core';
import { animate, group, query, style, transition, trigger } from '@angular/animations';
import { WizardStep } from './WizardStep';
import { WizardService } from './wizard.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { ButtonColor, ButtonSize } from '../../store/models/button.model';
import { DIALOG_COMPONENT, DialogComponent } from '../../shared/ui/dialog/dialog.component';
import { DialogButton } from '../../store/models/dialog.model';
import { ButtonDisablerService } from '../../store/services/button-disabler.service';

export interface IWizardButton {
  onClick: (wizardComponent: WizardComponent, dialog: DialogComponent) => void;
  text: string;
  color?: ButtonColor;
  style?: 'outline' | 'filled';
}

export interface IWizardStep {
  title: string;
  id: string;
  component?: any;
  canSkip?: boolean;
  componentData?: any;
  buttons?: IWizardButton[];
}

export const WIZARD_COMPONENT = new InjectionToken<WizardComponent>(
  'WizardComponent',
);

const left = [
  query(':enter, :leave', style({ width: '100%' }), { optional: true }),
  group([
    query(
      ':enter',
      [
        style({ transform: 'translateX(-100%)' }),
        animate('.3s ease-out', style({ transform: 'translateX(0%)' })),
      ],
      {
        optional: true,
      },
    ),
    query(
      ':leave',
      [
        style({ transform: 'translateX(0%)' }),
        animate('.3s ease-out', style({ transform: 'translateX(100%)' })),
      ],
      {
        optional: true,
      },
    ),
  ]),
];

const right = [
  query(':enter, :leave', style({ width: '100%' }), { optional: true }),
  group([
    query(
      ':enter',
      [
        style({ transform: 'translateX(100%)' }),
        animate('.3s ease-out', style({ transform: 'translateX(0%)' })),
      ],
      {
        optional: true,
      },
    ),
    query(
      ':leave',
      [
        style({ transform: 'translateX(0%)' }),
        animate('.3s ease-out', style({ transform: 'translateX(-100%)' })),
      ],
      {
        optional: true,
      },
    ),
  ]),
];

@Component({
  selector: 'app-wizard',
  templateUrl: './wizard.component.html',
  styleUrls: ['./wizard.component.scss'],
  animations: [
    trigger('animSlider', [
      transition(':increment', right),
      transition(':decrement', left),
    ]),
  ],
})
export class WizardComponent implements OnInit, OnDestroy {
  @Input() steps: IWizardStep[];

  @Input() startingStep: number;

  @Input() currentStepIndex = 0;

  @Input() showStepper = true;

  @Input() showButtons = true;

  @Output() finish: EventEmitter<any> = new EventEmitter<any>();

  stepErrors: { [key: string]: string[] } = {};

  validationInProgress: boolean;

  dcpProviders: StaticProvider[] = [
    {
      provide: WIZARD_COMPONENT,
      useValue: this,
    },
  ];

  nextStepDisabled: boolean;

  iconCheck = faCheck;

  buttonColors = ButtonColor;

  private stepInstances: {
    [key: number]: { component: WizardStep; data: any };
  } = {};

  private readonly destory$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private readonly wizardService: WizardService,
    @Inject(DIALOG_COMPONENT) private readonly dialogComponent: DialogComponent,
    private readonly buttonDisablerService: ButtonDisablerService,
  ) {
    this.wizardService
      .nextButtonDisabled()
      .pipe(takeUntil(this.destory$))
      .subscribe(state => {
        this.nextStepDisabled = state;
        this.setDialogButtons();
      });
  }

  ngOnInit() {
    if (this.startingStep) {
      this.currentStepIndex = this.startingStep;
    } else {
      this.startingStep = this.currentStepIndex;
    }

    this.setDialogButtons();
  }

  buildStepButtons(): DialogButton[] {
    return this.steps[this.currentStepIndex].buttons.map(button => {
      return {
        text: button.text,
        onClick: dialog => button.onClick(this, dialog),
        color: button.color || ButtonColor.BLUE,
        size: ButtonSize.NORMAL,
        style: button.style || 'filled',
      };
    });
  }

  setDialogButtons() {
    if (!this.showButtons && this.steps[this.currentStepIndex] && this.steps[this.currentStepIndex].buttons) {
      this.dialogComponent.setButtons(this.buildStepButtons());
      return;
    }

    const buttons: DialogButton[] = [
      {
        text: 'Cancel',
        size: ButtonSize.NORMAL,
        color: ButtonColor.RED,
        onClick: () => this.dialogComponent.close(),
      },
    ];

    if (
      this.currentStepIndex !== 0 &&
      this.currentStepIndex > this.startingStep
    ) {
      buttons.push({
        text: 'Back',
        size: ButtonSize.NORMAL,
        color: ButtonColor.BLUE,
        onClick: () => this.previousStep(),
      });
    }

    if (
      this.steps &&
      this.steps[this.currentStepIndex] &&
      this.steps[this.currentStepIndex].canSkip
    ) {
      buttons.push({
        text: 'Skip',
        size: ButtonSize.NORMAL,
        color: ButtonColor.BLUE,
        onClick: () => this.nextStep(false),
      });
    }

    buttons.push({
      text:
        this.steps && (this.currentStepIndex === this.steps.length - 1)
          ? 'Finish'
          : 'Next',
      size: ButtonSize.NORMAL,
      color: ButtonColor.BLUE,
      onClick: () => this.nextStep(),
      disabled: this.nextStepDisabled,
    });
    this.dialogComponent.setButtons(buttons);
  }

  collectCurrentStepData() {
    this.stepInstances[this.currentStepIndex].data =
      this.stepInstances[this.currentStepIndex].component.provideData();
  }

  getInstance<T extends WizardStep>(stepId: string): T {
    for (let i = 0; i < this.steps.length; i++) {
      const step = this.steps[i];
      if (step && step.id === stepId) {
        return this.stepInstances[i].component as T;
      }
    }

    return null;
  }

  goToStep(stepId: string): void {
    for (let i = 0; i < this.steps.length; i++) {
      const step = this.steps[i];
      if (step && step.id === stepId) {
        this.collectCurrentStepData();
        this.currentStepIndex = i;
        this.setDialogButtons();
        return;
      }
    }
  }

  notifyFinish(): void {
    this.finish.emit(this.collectComponentData());
  }

  async validateComponent() {
    const validation =
      await this.stepInstances[this.currentStepIndex].component.validate();

    if (!validation || !validation.success) {
      this.buttonDisablerService.disableActive();
    }

    return validation.success || false;
  }

  nextStep = async (validate: boolean = true) => {


    if (this.stepInstances[this.currentStepIndex]) {
      try {
        this.validationInProgress = true;
        const validation =
          await this.stepInstances[this.currentStepIndex].component.validate();
        this.validationInProgress = false;
        if (validate && !validation.success) {
          this.stepErrors[this.currentStepIndex] = validation.errors;
          this.buttonDisablerService.disableActive();
          this.setDialogButtons();
          return;
        } else {
          this.stepErrors[this.currentStepIndex] = null;
          this.stepInstances[this.currentStepIndex].data =
            this.stepInstances[this.currentStepIndex].component.provideData();
        }
        this.currentStepIndex++;
        this.wizardService.enableNextButton();
      } catch (e) {
        this.validationInProgress = false;
        this.stepErrors[this.currentStepIndex] = [e.message];
        this.buttonDisablerService.disableActive();
      }
    }

    if ((this.currentStepIndex - 1) === this.steps.length - 1) {
      this.notifyFinish();
      return;
    }

    this.setDialogButtons();
  };

  isComponent(input) {
    return typeof input !== 'string';
  }

  collectComponentData() {
    const data = {};

    Object.keys(this.stepInstances).forEach(
      step => (data[this.steps[step].id] = this.stepInstances[step].data),
    );
    return data;
  }

  onComponentRef($event: WizardStep, step: IWizardStep, index: number) {
    if (!this.stepInstances[index]) {
      this.stepInstances[index] = {
        component: $event,
        data: {},
      };
    } else {
      this.stepInstances[index].component = $event;
    }

    $event.wizardData = this.collectComponentData();
  }

  previousStep() {
    this.currentStepIndex--;
    this.wizardService.enableNextButton();
  }

  ngOnDestroy(): void {
    this.destory$.next(true);
  }
}
